// §11c / §5c — search terms that cost money and converted nothing.
//
// ⚠️⚠️ "SEARCH TERMS", NEVER "KEYWORDS", ON EVERY LINE OF THIS PANEL. A search
// term is what a person typed; a keyword is what you bid on. This system has
// search terms and NO KEYWORD DATA WHATSOEVER — no `keyword_view`, no
// `ad_group_criterion` spec, and `keyword_criterion` is a criterion resource id
// rather than keyword text. Calling these keywords would describe data we do
// not have, to the one audience that would act on the difference.
//
// ⚠️⚠️ AND WE CANNOT SEE EXISTING NEGATIVES, WHICH IS STATED ON THE PANEL. There
// is no negative-keyword data in the warehouse, so a term listed here may
// already be excluded and nothing here can tell. The framing is "worth
// REVIEWING as negatives" — presented as a checked recommendation list, a
// specialist would re-add negatives already in place and conclude the tool is
// wrong. That is a worse outcome than the panel not existing.
//
// ⚠️ AND CONVERSIONS ARE NEVER CORRECTED (§5g): the BigQuery transfer runs with
// no refresh window, so recent days carry their first-day conversion figure and
// under-report. A term that has since converted can appear here. An AM glancing
// weekly might not notice; a specialist judging yesterday every single morning
// would systematically misread it, so it is said on the panel rather than
// buried in a comment.

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { formatReportingAmount } from '../../lib/reportingMoney';
import type { GoogleSearchTermWaste } from '../../types/advertisingHome';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountLinkKey } from '../../lib/accountLinks';
import { PanelTitle } from '../reporting/PanelTitle';
import { googleReportingHref } from './deepLinks';

/** Shown at once; the rest stay reachable by narrowing the range or filters. */
const VISIBLE_ROWS = 25;

export function SearchTermWastePanel({
  rows,
  capped,
  nameOf,
  loading,
  linkKeys,
}: {
  linkKeys?: AccountLinkKey[];
  rows: readonly GoogleSearchTermWaste[];
  capped: boolean;
  nameOf: (accountId: string) => string;
  loading: boolean;
}): React.JSX.Element {
  const visible = rows.slice(0, VISIBLE_ROWS);
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        {/* ⚠️ THE WORDS. Not "wasted keywords". */}
        <PanelTitle>Search terms worth reviewing as negatives</PanelTitle>
        <span className="text-[10px] text-muted-foreground">cost, zero conversions</span>
      </div>

      {/* ⚠️⚠️ THE LIMITS LEAD THE FIGURES. A caveat read after a number has been
          believed is not a caveat, and both of these change what the list means. */}
      <p className="mb-3 text-[11px] text-muted-foreground">
        These are <strong className="font-medium">search terms</strong> — what people actually
        typed — not keywords.{' '}
        <strong className="font-medium">
          We cannot see which negatives are already in place
        </strong>
        , so some of these may already be excluded; treat it as a review list, not a checked
        recommendation. Google never re-sends conversion figures for a day once it has sent
        them, so a term that converted recently can still appear here.
      </p>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading search terms…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No search terms cost money without converting in this range.
        </p>
      ) : (
        <>
          <div className="scroll-subtle max-h-[28rem] overflow-y-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground shadow-[inset_0_-1px_0_hsl(var(--border))]">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Search term</th>
                  <th className="px-2 py-1.5 text-left font-medium">Account</th>
                  <th className="px-2 py-1.5 text-right font-medium">Cost</th>
                  <th className="px-2 py-1.5 text-right font-medium">Clicks</th>
                  <th className="px-2 py-1.5 text-right font-medium">Impr.</th>
                  <th className="px-2 py-1.5 text-left font-medium">Match</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visible.map((r) => (
                  <tr key={`${r.accountId}-${r.searchTerm}-${r.currency}`} className="hover:bg-accent/40">
                    <td className="max-w-[20rem] px-2 py-1.5">
                      <span className="block truncate" title={r.searchTerm}>
                        {r.searchTerm}
                      </span>
                      {r.campaigns > 1 && (
                        <span className="text-[11px] text-muted-foreground">
                          across {r.campaigns} campaigns
                        </span>
                      )}
                    </td>
                    <td className="max-w-[14rem] px-2 py-1.5">
                      <span className="flex min-w-0 items-center gap-1">
                        <span className="truncate" title={nameOf(r.accountId)}>
                          {nameOf(r.accountId)}
                        </span>
                        {linkKeys && (
                          <AccountLinkIcons accountId={r.accountId} className="shrink-0" only={linkKeys} />
                        )}
                      </span>
                    </td>
                    {/* ⚠️ Native currency, never blended — the panel spans
                        accounts in AUD and NZD and a sum would be of nothing. */}
                    <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                      {formatReportingAmount(r.cost, r.currency)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                      {r.clicks}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                      {r.impressions}
                    </td>
                    <td className="px-2 py-1.5 text-[11px] text-muted-foreground">
                      {r.matchType ?? '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Link
                        to={googleReportingHref(r.accountId)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-kyck-700 hover:underline"
                      >
                        View
                        <ArrowRight className="h-3 w-3" aria-hidden />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* ⚠️ A TRUNCATED LIST IS NEVER PRESENTED AS THE WHOLE PICTURE — the
              same rule the search-terms panel's own `totalTerms` exists for. */}
          {(rows.length > visible.length || capped) && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Showing the {visible.length} costliest
              {capped
                ? ' of the costliest 200 the query returns — there is more than this. Narrow the range or the filters to see further down.'
                : ` of ${rows.length}.`}
            </p>
          )}
        </>
      )}
    </div>
  );
}
